
import {
  computed, defineComponent, reactive, ref,
} from 'vue';
import { IOption } from '@/core/models/AntDesign/IOption';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import storeSistema from '@/store/storeSistema';
import { IParametrosConsulta } from '@/core/models/Consulta/IParametrosConsulta';
import ServicoTipoProducao from '@/servicos/Cadastros/PCP/ServicoTipoProducao';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';

export default defineComponent({
  name: 'SelecionarTipoProducao',
  props: {
    varios: {
      type: Boolean,
      default: false,
    },
    codigoSelecionado: {
      type: Number || undefined,
    },
    codigosSelecionados: {
      type: Array as () => number[],
      default: () => [],
    },
    descricaoSelecionado: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Selecione',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    tipoProducao: {
      type: Array as () => number[],
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'update:descricaoSelecionado', 'change'],
  setup(props, { emit }) {
    const { montaOpcoesComListaConsultaRapida } = useTelaBase();
    const refSelecionarTipoProducao = ref<HTMLElement | null>(null);

    let debounce = 0;
    const servicoTipoProducao = new ServicoTipoProducao();
    servicoTipoProducao.requisicaoSistema();

    const state = reactive({
      listaTipoProducao: [] as IOption[],
      buscandoDados: false,
      emDigitacao: false,
    });

    const computedDescricaoSelecionado = computed({
      get: () => props.descricaoSelecionado,
      set: (val: string) => {
        emit('update:descricaoSelecionado', val);
      },
    });

    async function obterTipoProducaoPorCodigo(valor: any) {
      if (state.emDigitacao) { return; }
      if (!UtilitarioGeral.valorValido(valor)) { return; }
      if (!(valor > 0)) { return; }

      if (props.varios) {
        if (props.codigosSelecionados.length === 0) { return; }
      } else if (props.codigoSelecionado === 0) {
        return;
      }
      let efetuarConsulta = true;

      if (UtilitarioGeral.validaLista(state.listaTipoProducao)) {
        if (props.varios) {
          const listaCodigosConsulta = state.listaTipoProducao.map((c) => c.value);
          const diferencas = props.codigosSelecionados.filter((c) => !listaCodigosConsulta.includes(c));
          if (!UtilitarioGeral.validaLista(diferencas)) {
            efetuarConsulta = false;
          }
        } else {
          const existeLista = state.listaTipoProducao.find((c) => c.value === props.codigoSelecionado);
          if (existeLista !== undefined) {
            efetuarConsulta = false;
          }
        }

        const option = state.listaTipoProducao.find((c) => c.value === valor);
        if (option?.label !== undefined && option?.label !== '') {
          computedDescricaoSelecionado.value = option?.label;
        }
      }

      if (!efetuarConsulta) return;

      state.listaTipoProducao = [];

      const parametrosConsultaRapida = {} as IParametrosConsulta;

      if (UtilitarioGeral.validaLista(props.empresas)) {
        parametrosConsultaRapida.empresas = props.empresas;
      } else {
        parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
      }

      if (UtilitarioGeral.validaLista(props.tipoProducao)) {
        parametrosConsultaRapida.codigosSelecionados = props.tipoProducao;
      }

      const listaTipoProducao = await servicoTipoProducao.consultaRapida(parametrosConsultaRapida);
      state.listaTipoProducao = montaOpcoesComListaConsultaRapida(listaTipoProducao);
      if (!UtilitarioGeral.validaLista(listaTipoProducao)) {
        if (props.varios) {
          emit('update:codigosSelecionados', undefined);
        } else {
          emit('update:codigoSelecionado', undefined);
        }
      }
    }

    async function pesquisarTipoProducao(valorPesquisa: any) {
      state.emDigitacao = true;

      state.buscandoDados = true;
      state.listaTipoProducao = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida: IParametrosConsultaRapida = {
          valor: valorPesquisa, apenasAtivos: true, recursoAssociado: '', quantidadeRegistros: 100,
        };

        if (UtilitarioGeral.validaLista(props.empresas)) {
          parametrosConsultaRapida.empresas = props.empresas;
        } else {
          parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
        }

        const listaTipoProducao = await servicoTipoProducao.consultaRapida(parametrosConsultaRapida);
        state.buscandoDados = false;
        state.listaTipoProducao = montaOpcoesComListaConsultaRapida(listaTipoProducao);
      }, 600);
    }

    function obterTipoProducao():any {
      obterTipoProducaoPorCodigo(props.codigoSelecionado);
      return (props.codigoSelecionado === 0 ? undefined : props.codigoSelecionado);
    }

    const computedCodigoSelecionado = computed({
      get: () => obterTipoProducao(),
      set: (val: number) => {
        emit('update:codigoSelecionado', val);
        const option = state.listaTipoProducao.find((c) => c.value === val);
        if (option?.label !== undefined && option?.label !== '') {
          computedDescricaoSelecionado.value = option?.label;
        }
      },
    });

    function obterTiposProducao():number[] {
      obterTipoProducaoPorCodigo(props.codigosSelecionados);
      return props.codigosSelecionados;
    }

    const computedCodigosSelecionados = computed({
      get: () => obterTiposProducao(),
      set: (val: number[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    function change() {
      emit('change', props.varios ? props.codigosSelecionados : props.codigoSelecionado);
    }

    function focus() {
      if (refSelecionarTipoProducao.value) {
        refSelecionarTipoProducao.value.focus();
      }
    }

    return {
      props,
      state,
      refSelecionarTipoProducao,
      pesquisarTipoProducao,
      computedCodigoSelecionado,
      computedCodigosSelecionados,
      computedDescricaoSelecionado,
      change,
      focus,
    };
  },
});
